import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useToast } from '../../../context/ToastContext'
import { useReviewsAggregate } from '../../../hooks/useReviews'
import { StarRatingDisplay } from '../reviews/StarRating'

export default function AstrologerProfileHeroSection({ astro }) {
  const [isFav, setIsFav] = useState(false)
  const { showToast } = useToast()

  // The astrologer record's own rating/reviews_count fields aren't kept in
  // sync upstream (verified live: they come back null even when real
  // reviews exist) — the live GET /reviews list is the source of truth.
  const { summary: reviewSummary } = useReviewsAggregate('astrologer', astro.id)
  const liveRating = reviewSummary.averageRating
  const liveReviewCount = reviewSummary.totalReviews
  const liveRatingLabel = liveReviewCount > 0 ? liveRating.toFixed(1) : 'New'

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ title: astro.name, url })
        return
      } catch {
        // user cancelled the native share sheet — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      showToast('Profile link copied to clipboard')
    } catch {
      showToast('Could not copy link')
    }
  }

  return (
    <>
      {/* BREADCRUMB */}
      <nav
        aria-label="Breadcrumb"
        className="max-w-350 mx-auto px-4 sm:px-6 lg:px-12 pt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-[#8B7355]"
      >
        <Link to="/" className="hover:text-secondary transition-colors">Home</Link>
        <span className="opacity-40">/</span>
        <Link to="/consultations" className="hover:text-secondary transition-colors">Astrologers</Link>
        <span className="opacity-40">/</span>
        <span className="text-[#1C140A] font-semibold normal-case truncate">{astro.name}</span>
      </nav>

      {/* PROFILE HERO */}
      <section className="relative max-w-350 mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="info-card p-6 sm:p-10">
          <div className="grid gap-10 grid-cols-1 lg:grid-cols-12 items-stretch">

            {/* Photo + status */}
            <div className="flex flex-col gap-5 h-full lg:col-span-4">
              <div className="relative overflow-hidden rounded-[28px] border border-[#D7C29A]/25 shadow-lg flex-1 min-h-80">
                {astro.image ? (
                  <img
                    src={astro.image}
                    alt={astro.name}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    onError={(e) => { e.currentTarget.style.display = 'none' }}
                  />
                ) : (
                  <div
                    className="absolute inset-0 flex items-center justify-center font-cinzel text-8xl font-bold"
                    style={{ background: astro.fallbackGradient, color: 'rgba(120,50,0,.5)' }}
                  >
                    {astro.fallbackLetter}
                  </div>
                )}
                <div className="absolute top-4 left-4 hero-card-badge">
                  <span className="text-secondary">✦</span> Verified
                </div>
                {astro.online && (
                  <div className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Online now
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="space-y-6 lg:col-span-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="hero-chip mb-4">
                    <span className="text-secondary">✦</span> {astro.role}
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-cinzel font-semibold text-[#1C140A]">{astro.name}</h1>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#5C3A18]">
                    <span className="flex items-center gap-1.5">
                      <StarRatingDisplay value={liveRating} size="md" />
                      <span className="font-semibold text-[#1C140A]">{liveRatingLabel}</span>
                      <span className="text-[#8B7355]">({liveReviewCount} review{liveReviewCount === 1 ? '' : 's'})</span>
                    </span>
                    {astro.languages.length > 0 && (
                      <>
                        <span className="w-px h-4 bg-[#D7C29A]/40" />
                        <span>{astro.languages.join(', ')}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Save astrologer"
                    onClick={() => setIsFav((v) => !v)}
                    className="w-11 h-11 rounded-full border border-[#D7C29A]/30 bg-white flex items-center justify-center hover:border-secondary hover:text-secondary transition-colors"
                    style={isFav ? { color: '#ef4444', borderColor: 'rgba(239,68,68,.4)' } : undefined}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Share profile"
                    onClick={handleShare}
                    className="w-11 h-11 rounded-full border border-[#D7C29A]/30 bg-white flex items-center justify-center hover:border-secondary hover:text-secondary transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                    </svg>
                  </button>
                </div>
              </div>

              {astro.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {astro.skills.map((skill, i) => (
                    <span key={skill} className={`pill-toggle${i === 0 ? ' active' : ''}`}>{skill}</span>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="hero-stat-block text-center">
                  <h4 className="text-xl font-semibold">{liveReviewCount}</h4>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Reviews</p>
                </div>
                <div className="hero-stat-block text-center">
                  <h4 className="text-xl font-semibold">{astro.experience}+</h4>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Years exp.</p>
                </div>
                <div className="hero-stat-block text-center">
                  <h4 className="text-xl font-semibold">{astro.duration} min</h4>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Per session</p>
                </div>
                <div className="hero-stat-block text-center">
                  <h4 className="text-xl font-semibold">{liveRatingLabel}★</h4>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-[#8B7355]">Rating</p>
                </div>
              </div>

              <div className="astro-divider" />

              <div className="flex flex-wrap items-center justify-between gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#8B7355]">Starting from</p>
                  <p className="mt-1 text-2xl font-bold text-[#C8922A]">
                    {astro.priceLabel} <span className="text-sm font-medium text-[#8B7355]">/ session</span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href="tel:+919091840899" className="hero-btn-secondary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.2em]">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25V16.14a2.25 2.25 0 0 0-1.545-2.14l-4.22-1.354a2.25 2.25 0 0 0-2.586 1.022l-1.411 2.334a15.682 15.682 0 0 1-6.75-6.75l2.334-1.411a2.25 2.25 0 0 0 1.022-2.586l-1.354-4.22A2.25 2.25 0 0 0 6.14 2.25H4.5a2.25 2.25 0 0 0-2.25 2.25v2.25Z" />
                    </svg>
                    Call
                  </a>
                  <a href="#book-slot" className="hero-btn-primary inline-flex items-center justify-center rounded-full px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.25em]">
                    Book Consultation
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
